import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { firebaseConfigured, sendVerification, resendVerification as resendFirebaseEmail, verifiedIdToken, friendlyFirebaseError } from "../../services/firebaseAuth.js";
import {
  Bot, Users, Search, BookOpen, GitPullRequest, Lightbulb,
  Copy, Check, Sparkle, Database, Box, Play, ArrowRight, ChevronRight,
  ChartNoAxesColumn, Plug, Shield, UserCheck, Calendar, LayoutGrid, ListChecks,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const INSTALL_COMMANDS = {
  npm: { install: "npm install -g agent-lyra", init: "agent-lyra init" },
  pnpm: { install: "pnpm add -g agent-lyra", init: "agent-lyra init" },
  pip: { install: "pip install agent-lyra", init: "agent-lyra init" },
};

const FLOW = [
  { Icon: Database, label: "Connectors", cap: "Bring your data together" },
  { Icon: Box, label: "Lyra Vault", cap: "Organize and ground context" },
  { Icon: Sparkle, label: "Domain Agents", cap: "Turn knowledge into action", active: true },
  { Icon: Users, label: "Human Teams", cap: "Collaborate with people" },
  { Icon: Play, label: "Auto-pilot", cap: "Run work on schedule" },
];

const AGENT_CARDS = [
  { key: "workforce", Icon: Users, name: "Workforce", sub: "Talent acquisition", desc: "Find, engage, and hire the right talent faster with intelligent agents.", tint: "#eeeafe", color: "#6d5ef0" },
  { key: "atlas", Icon: Box, name: "Lyra Atlas", sub: "Engineering intelligence", desc: "Turn engineering knowledge into faster, higher-quality decisions.", tint: "#e6f0ff", color: "#3b82f6" },
  { key: "ledger", Icon: ChartNoAxesColumn, name: "Lyra Ledger", sub: "Finance intelligence", desc: "Close faster, plan smarter, and surface what matters with trusted financial insights.", tint: "#e3f8ee", color: "#10a36b" },
];

const STEPS = [
  [Database, "Integrations", "Connect your tools and bring your data together."],
  [BookOpen, "RAG knowledge", "Organize and retrieve trusted knowledge from your business."],
  [Sparkle, "Domain agents", "Agents reason, plan, and take action with your context."],
  [UserCheck, "Approvals", "Keep humans in the loop with transparent review and controls."],
  [Calendar, "Scheduled execution", "Run work automatically on your schedule, with full audit trails."],
];

const PLATFORM = [
  { title: "Build", sub: "Create and customize agents for your business.", Icon: LayoutGrid, tint: "#f1eefe", color: "#6d5ef0", items: [
    [Plug, "Connectors", "Link your data, tools, and applications."],
    [Sparkle, "Custom Agents", "Build domain-specific agents, no code required."],
    [LayoutGrid, "Skills", "Extend agents with new capabilities."],
  ] },
  { title: "Govern", sub: "Maintain control, trust, and accountability.", Icon: Shield, tint: "#eef3ff", color: "#3b6fe0", items: [
    [Box, "Lyra Vault", "Securely organize your business context."],
    [Users, "Teams", "Manage access, roles, and collaboration."],
    [UserCheck, "Approvals", "Set guardrails with review and audit trails."],
  ] },
  { title: "Execute", sub: "Turn plans into real progress.", Icon: Play, tint: "#e9faf2", color: "#10a36b", items: [
    [Play, "Auto-pilot", "Let agents run recurring work end to end."],
    [Calendar, "Scheduled execution", "Set it and let it run, on your terms."],
    [ListChecks, "Integrations", "Sync results back to your systems."],
  ] },
];

const KANBAN = [
  { title: "To do", n: 3, cards: [["Draft hiring plan", "Workforce", "#8b7cf6"], ["Analyze cost drivers", "Lyra Ledger", "#10a36b"], ["Improve onboarding", "Workforce", "#8b7cf6"]] },
  { title: "In progress", n: 2, cards: [["Investigate production issue", "Lyra Atlas", "#3b82f6"], ["Prepare budget review", "Lyra Ledger", "#10a36b"]] },
  { title: "Done", n: 3, cards: [["Update candidate pipeline", "Workforce", "#8b7cf6"], ["Ship new deployment flow", "Lyra Atlas", "#3b82f6"], ["Create finance report", "Lyra Ledger", "#10a36b"]] },
];

const PLANNER = [
  ["Mon", ["Draft hiring plan", "Review PRs"]],
  ["Tue", ["Analyze cost drivers"]],
  ["Wed", ["Prepare budget review", "Candidate screen"]],
  ["Thu", ["Investigate production issue"]],
  ["Fri", ["Create finance report", "Ship deployment flow"]],
];

const TIMELINE = [
  ["Workforce · Screening", 5, 40, "#8b7cf6"],
  ["Lyra Atlas · Incident review", 25, 35, "#3b82f6"],
  ["Lyra Ledger · Variance report", 45, 40, "#10a36b"],
  ["Auto-pilot · Weekly digest", 15, 70, "#6d5ef0"],
];

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// ── Animated section wrapper ───────────────────────────────────────────────────
function FadeUp({ children, delay = 0, style = {} }) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      ...style
    }}>
      {children}
    </div>
  );
}

// \u2500\u2500 Auth Modal \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
function Modal({ open, onClose, initialTab = "login", onLogin }) {
  const [tab, setTab] = useState(initialTab);
  // Set once an account exists but its email is not yet verified: { email, password }. Firebase sends the link.
  const [pending, setPending] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  useEffect(() => { setTab(initialTab); setError(""); setSuccess(""); setPending(null); }, [initialTab, open]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleGoogleOAuth() {
    try {
      // Server returns a JSON { authUrl } — it does NOT do a GET redirect
      const res = await fetch(`${API}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ state: JSON.stringify({ returnTo: "/dashboard" }) }),
      });
      const data = await res.json();
      if (data.authUrl) {
        window.location.href = data.authUrl; // redirect to Google consent screen
      } else {
        setError("Google sign-in unavailable. Use email/password instead.");
      }
    } catch {
      setError("Google sign-in unavailable. Please try email/password.");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!form.email.trim() || !form.password.trim()) { setError("Please fill in all fields."); return; }
    if (tab === "signup" && !form.name.trim()) { setError("Please enter your name."); return; }
    if (tab === "signup" && form.password.length < 6) { setError("Password must be at least 6 characters."); return; }

    setLoading(true);
    try {
      const endpoint = tab === "login" ? "/auth/login" : "/auth/register";
      const body = tab === "login"
        ? { email: form.email.trim(), password: form.password }
        : { name: form.name.trim(), email: form.email.trim(), password: form.password };

      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (res.status === 403 && data.code === "EMAIL_NOT_VERIFIED") {
        await beginVerification(data.email || form.email.trim(), form.password);
        return;
      }

      if (!res.ok) {
        setError(data.message || data.error || (tab === "login" ? "Login failed." : "Registration failed."));
        return;
      }

      if (data.requiresVerification) {
        await beginVerification(data.email || form.email.trim(), form.password);
        return;
      }

      if (data.token) {
        localStorage.setItem("authToken", data.token);
        if (onLogin) onLogin(data.token);
        onClose();
        navigate("/dashboard");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Has Firebase email the verification link, then shows the "check your inbox" step.
  async function beginVerification(email, password) {
    if (!firebaseConfigured) { setError("Email verification is not available right now. Please try again later."); return; }
    try {
      const r = await sendVerification(email, password);
      setPending({ email, password });
      if (r.verified) setSuccess("Your email is already verified. Press Continue.");
    } catch (e) {
      setError(friendlyFirebaseError(e));
    }
  }

  async function resendVerification() {
    setError(""); setSuccess(""); setLoading(true);
    try {
      await resendFirebaseEmail();
      setSuccess("Verification email sent.");
    } catch (e) {
      setError(friendlyFirebaseError(e));
    } finally {
      setLoading(false);
    }
  }

  // "I've verified": Firebase confirms the link was opened, and the server trades its token for a Lyra session.
  async function continueAfterVerify() {
    setError(""); setSuccess(""); setLoading(true);
    try {
      const idToken = await verifiedIdToken();
      if (!idToken) { setError("Still not verified. Open the link in your email (check spam), then try again."); return; }
      const res = await fetch(`${API}/auth/firebase-session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem("authToken", data.token);
        if (onLogin) onLogin(data.token);
        onClose();
        navigate("/dashboard");
        return;
      }
      setError(data.message || "Sign-in failed.");
    } catch (e) {
      setError(friendlyFirebaseError(e));
    } finally {
      setLoading(false);
    }
  }

  const s = {
    overlay: { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,.6)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, animation: "fadeIn .2s ease" },
    box: { background: "var(--lyra-color-surface)", borderRadius: 24, width: "100%", maxWidth: 420, boxShadow: "0 32px 80px rgba(0,0,0,.2)", overflow: "hidden", position: "relative", animation: "slideUp .25s cubic-bezier(.22,1,.36,1)" },
    closeBtn: { position: "absolute", top: 14, right: 14, background: "var(--lyra-color-surface-2)", border: "none", cursor: "pointer", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "var(--lyra-color-text-muted)" },
    header: { padding: "28px 28px 0", textAlign: "center" },
    logoMark: { width: 48, height: 48, borderRadius: 14, background: "linear-gradient(135deg,var(--app-primary-mid),var(--app-primary-deep))", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 22, color: "var(--app-on-accent)", margin: "0 auto 16px", boxShadow: "0 8px 20px rgba(58,107,92,.35)" },
    title: { fontFamily: "'DM Sans',sans-serif", fontSize: 22, fontWeight: 800, color: "var(--lyra-color-text)", marginBottom: 6 },
    sub: { fontSize: 13.5, color: "var(--lyra-color-text-muted)", marginBottom: 0 },
    tabs: { display: "flex", background: "var(--lyra-color-surface-2)", borderRadius: 10, padding: 4, margin: "20px 28px 0", gap: 4 },
    tab: (a) => ({ flex: 1, padding: "8px", borderRadius: 7, border: "none", fontSize: 13.5, fontWeight: 600, cursor: "pointer", transition: "all .15s", fontFamily: "'DM Sans',sans-serif", background: a ? "var(--lyra-color-surface)" : "transparent", color: a ? "var(--lyra-color-text)" : "var(--lyra-color-text-muted)", boxShadow: a ? "0 1px 3px rgba(0,0,0,.06)" : "none" }),
    body: { padding: "24px 28px 28px", display: "flex", flexDirection: "column", gap: 14 },
    googleBtn: { width: "100%", padding: 11, borderRadius: 11, border: "1.5px solid var(--lyra-color-border)", fontSize: 14, fontWeight: 600, color: "var(--lyra-color-text)", background: "var(--lyra-color-surface)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 9, transition: "all .15s" },
    divider: { display: "flex", alignItems: "center", gap: 12, color: "#94a3b8", fontSize: 12.5 },
    label: { display: "block", fontSize: 13, fontWeight: 600, color: "var(--lyra-color-text)", marginBottom: 6 },
    input: { width: "100%", padding: "10px 14px", border: "1.5px solid var(--lyra-color-border)", borderRadius: 10, fontSize: 14, color: "var(--lyra-color-text)", outline: "none", background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans',sans-serif", boxSizing: "border-box", transition: "border-color .15s" },
    submit: { width: "100%", padding: 13, borderRadius: 11, border: "none", fontSize: 14.5, fontWeight: 700, color: "var(--app-on-accent)", background: "var(--app-primary-mid)", cursor: loading ? "not-allowed" : "pointer", fontFamily: "'DM Sans',sans-serif", boxShadow: "0 4px 14px rgba(58,107,92,.3)", opacity: loading ? 0.7 : 1 },
    footerText: { textAlign: "center", fontSize: 12.5, color: "#94a3b8" },
    errBox: { background: "var(--lyra-color-danger-bg)", border: "1px solid var(--app-danger-border)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--lyra-color-danger)" },
    succBox: { background: "var(--lyra-color-success-bg)", border: "1px solid var(--app-success-border)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--lyra-color-success)" },
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.box}>
        <button style={s.closeBtn} onClick={onClose}>✕</button>
        <div style={s.header}>
          <div style={s.logoMark}>W</div>
          <div style={s.title}>{tab === "login" ? "Welcome back" : "Create your workspace"}</div>
          <div style={s.sub}>{tab === "login" ? "Sign in to your Lyra workspace" : "Free forever · No credit card needed"}</div>
        </div>
        {pending ? (
          <div style={{ ...s.body, paddingTop: 20 }}>
            <div style={{ textAlign: "center", fontSize: 14, color: "var(--lyra-color-text)", lineHeight: 1.5 }}>
              <b>Verify your email</b><br />
              We sent a verification link to <b>{pending.email}</b>. Open it, then come back and continue.
            </div>
            {error && <div style={s.errBox}>{error}</div>}
            {success && <div style={s.succBox}>{success}</div>}
            <button type="button" style={s.submit} disabled={loading} onClick={continueAfterVerify}>I've verified — Continue</button>
            <button type="button" style={s.googleBtn} disabled={loading} onClick={resendVerification}>Resend verification email</button>
            <div style={s.footerText}><a href="#" onClick={e => { e.preventDefault(); setPending(null); setError(""); setSuccess(""); setTab("login"); }} style={{ color: "var(--app-primary-mid)", fontWeight: 600 }}>Back to sign in</a></div>
          </div>
        ) : (<>
        <div style={s.tabs}>
          <button style={s.tab(tab === "login")} onClick={() => { setTab("login"); setError(""); setSuccess(""); }}>Log in</button>
          <button style={s.tab(tab === "signup")} onClick={() => { setTab("signup"); setError(""); setSuccess(""); }}>Sign up</button>
        </div>
        <form style={s.body} onSubmit={handleSubmit}>
          <button type="button" style={s.googleBtn} onClick={handleGoogleOAuth} disabled={loading}>
            <GoogleIcon />{tab === "login" ? "Continue with Google" : "Sign up with Google"}
          </button>
          <div style={s.divider}><div style={{ flex: 1, height: 1, background: "var(--lyra-color-border)" }} /> or <div style={{ flex: 1, height: 1, background: "var(--lyra-color-border)" }} /></div>

          {error && <div style={s.errBox}>{error}</div>}
          {success && <div style={s.succBox}>{success}</div>}

          {tab === "signup" && (
            <div><label style={s.label}>Full name</label>
              <input style={s.input} placeholder="Your name" value={form.name} onChange={set("name")}
                onFocus={e => e.target.style.borderColor = "var(--app-primary-mid)"} onBlur={e => e.target.style.borderColor = "var(--lyra-color-border)"} />
            </div>
          )}
          <div><label style={s.label}>Email</label>
            <input style={s.input} type="email" placeholder="you@example.com" value={form.email} onChange={set("email")}
              onFocus={e => e.target.style.borderColor = "var(--app-primary-mid)"} onBlur={e => e.target.style.borderColor = "var(--lyra-color-border)"} />
          </div>
          <div><label style={s.label}>Password</label>
            <input style={s.input} type="password" placeholder={tab === "signup" ? "Min 6 characters" : "••••••••"} value={form.password} onChange={set("password")}
              onFocus={e => e.target.style.borderColor = "var(--app-primary-mid)"} onBlur={e => e.target.style.borderColor = "var(--lyra-color-border)"} />
          </div>
          <button type="submit" style={s.submit} disabled={loading}>
              {tab === "login" ? "Sign in to Lyra" : "Create free workspace"}
          </button>
          {tab === "login"
            ? <div style={s.footerText}>Don't have an account? <a href="#" onClick={e => { e.preventDefault(); setTab("signup"); setError(""); }} style={{ color: "var(--app-primary-mid)", fontWeight: 600, textDecoration: "none" }}>Sign up free</a></div>
            : <div style={s.footerText}>Already have an account? <a href="#" onClick={e => { e.preventDefault(); setTab("login"); setError(""); }} style={{ color: "var(--app-primary-mid)", fontWeight: 600, textDecoration: "none" }}>Log in</a></div>
          }
        </form>
        </>)}
      </div>
    </div>
  );
}


const DEMOS = {
  research: {
    label: "Research Agent",
    icon: <Search size={16} />,
    subtitle: "Deep research & insights",
    title: "Research anything. Get a decision, not 20 tabs.",
    placeholder: "e.g. Compare the best laptops under ₹2L for local AI development",
    defaultInput: "Compare the best laptops under ₹2L for local AI development",
    steps: ["Understanding request", "Planning research", "Finding sources", "Analyzing information", "Synthesizing results"],
    resultTitle: "Laptop shortlist for local AI development",
    resultIntro: "Lyra compared performance, RAM, GPU capability, thermals and value across the most relevant options.",
    resultRows: [
      ["MacBook Air M3", "16GB · 512GB", "Best efficiency", "Excellent local inference for smaller models"],
      ["ASUS Zephyrus G14", "RTX 4050 · 16GB", "Best GPU", "Better for CUDA workloads and experimentation"],
      ["Lenovo Legion Slim 5", "RTX 4060 · 16GB", "Best performance", "More headroom for heavier local workloads"],
    ],
    footer: "12 sources analyzed · 24s",
  },
  news: {
    label: "News Brief",
    icon: <BookOpen size={16} />,
    subtitle: "Personal intelligence",
    title: "Turn your interests into a briefing that keeps itself current.",
    placeholder: "e.g. AI agents, open-source LLMs and e-commerce — India + global",
    defaultInput: "AI agents, open-source LLMs and e-commerce — India + global",
    steps: ["Understanding interests", "Finding relevant sources", "Removing duplicates", "Ranking importance", "Writing your briefing"],
    resultTitle: "Your AI & e-commerce intelligence brief",
    resultIntro: "Lyra found the highest-signal developments and grouped them by what is actually worth your attention.",
    resultRows: [
      ["Agent platforms", "High signal", "New agent frameworks are converging on durable workflows and tool use."],
      ["Open models", "Watch", "Smaller capable models continue to make local inference more practical."],
      ["Commerce AI", "High signal", "Shopping agents are moving from recommendations toward task completion."],
    ],
    footer: "9 sources ranked · 3 stories selected",
  },
  career: {
    label: "Career Agent",
    icon: <Users size={16} />,
    subtitle: "Resume & job match",
    title: "Give Lyra a profile and a goal. Let it close the gap.",
    placeholder: "Paste a LinkedIn profile + job description",
    defaultInput: "Senior Software Engineer — AI/ML platform experience\nTarget: Staff AI Engineer",
    steps: ["Reading your profile", "Understanding the target role", "Finding skill gaps", "Reframing your experience", "Preparing next steps"],
    resultTitle: "Staff AI Engineer — fit analysis",
    resultIntro: "Lyra matched your experience against the role and turned the gaps into an actionable preparation plan.",
    resultRows: [
      ["Strong match", "Architecture", "Production systems, distributed services and technical ownership"],
      ["Strong match", "AI systems", "Agent workflows, retrieval and model integration experience"],
      ["Focus next", "Leadership", "Make cross-team technical influence more explicit in the resume"],
    ],
    footer: "Profile + role analyzed · Action plan ready",
  },
  code: {
    label: "Code Agent",
    icon: <GitPullRequest size={16} />,
    subtitle: "Review, explain & improve",
    title: "Don't just review code. Let an agent investigate it.",
    placeholder: "Paste a function, API handler or small code snippet",
    defaultInput: `async function getUser(id) {
  const user = await db.users.findOne({ id });
  return user;
}`,
    steps: ["Reading the code", "Tracing likely failure modes", "Checking patterns", "Planning improvements", "Delivering changes"],
    resultTitle: "Code investigation complete",
    resultIntro: "Lyra found the likely risks, explained why they matter and turned the review into concrete engineering actions.",
    resultRows: [
      ["Risk", "Input validation", "Validate and normalize the id before querying."],
      ["Risk", "Error handling", "Handle missing users and database failures explicitly."],
      ["Improve", "Observability", "Add structured logging around the lookup path."],
    ],
    footer: "Static analysis + reasoning complete · 3 actions",
  },
  surprise: {
    label: "Surprise Me",
    icon: <Lightbulb size={16} />,
    subtitle: "Give Lyra a challenge",
    title: "Give Lyra a vague goal. See what it does with it.",
    placeholder: "e.g. Help me plan a better way to learn something new",
    defaultInput: "I want to learn something useful this month. Surprise me.",
    steps: ["Understanding the goal", "Breaking it into a plan", "Choosing an approach", "Creating useful actions", "Delivering the challenge"],
    resultTitle: "Your 30-day learning challenge",
    resultIntro: "Lyra turned a vague intention into a small, achievable workflow with checkpoints and a tangible outcome.",
    resultRows: [
      ["Week 1", "Explore", "Choose one problem and spend 30 minutes a day understanding it."],
      ["Week 2", "Build", "Create one tiny working artifact instead of collecting tutorials."],
      ["Week 3", "Share", "Explain what you learned and get feedback from someone else."],
    ],
    footer: "Goal converted into a workflow · 30-day plan",
  },
};

const LP_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.lp{--lyra-color-surface:#fff;--lyra-color-surface-2:#f5f6fd;--lyra-color-border:#e4e6f4;--lyra-color-text:#12142b;--lyra-color-text-muted:#5d6280;--lyra-color-primary:#6d5ef0;--lyra-color-danger:#dc2626;--lyra-color-danger-bg:#fef2f2;--lyra-color-success:#0f9d6a;--lyra-color-success-bg:#e3f8ee;--app-primary-mid:#5b4be8;--app-primary-deep:#4338ca;--app-primary-soft:#eeeafe;--app-primary-soft-2:#a79bf5;--app-on-accent:#fff;--app-danger-border:#fecaca;--app-surface-3:#eceefa;
  font-family:'Inter',sans-serif;background:#f7f8fe;color:#12142b;overflow-x:hidden;line-height:1.5}
.lp *{box-sizing:border-box;margin:0;padding:0}
.lp button{font-family:inherit}
.lp a{text-decoration:none;color:inherit}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideUp{from{opacity:0;transform:translateY(20px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
.lp-btn{display:inline-flex;align-items:center;gap:8px;border:none;cursor:pointer;font-weight:600;font-size:13px;color:#fff;background:linear-gradient(135deg,#6d5ef0,#5b4be8);padding:10px 18px;border-radius:10px;box-shadow:0 6px 18px rgba(91,75,232,.4);transition:transform .15s}
.lp-btn:hover{transform:translateY(-1px)}
.lp-nav{position:absolute;top:0;left:0;right:0;z-index:20;max-width:1240px;margin:0 auto;padding:0 28px;height:64px;display:flex;align-items:center;justify-content:space-between;color:#fff}
.lp-nav-links{display:flex;gap:28px;margin-left:40px;flex:1;font-size:11px;color:rgba(255,255,255,.75)}
.lp-nav-links a:hover{color:#fff}
.lp-brand{display:flex;align-items:center;gap:8px;font-weight:700;font-size:15px}
.lp-hero{position:relative;background:radial-gradient(ellipse at 70% 30%,#1d1f66 0%,#0e1038 45%,#080a26 100%);color:#fff;padding:96px 0 150px;overflow:hidden}
.lp-hero::before{content:'';position:absolute;inset:0;background-image:radial-gradient(1.5px 1.5px at 20% 30%,rgba(255,255,255,.7),transparent),radial-gradient(1px 1px at 60% 20%,rgba(255,255,255,.6),transparent),radial-gradient(1.5px 1.5px at 80% 60%,rgba(140,130,255,.9),transparent),radial-gradient(1px 1px at 40% 70%,rgba(255,255,255,.5),transparent),radial-gradient(1px 1px at 90% 15%,rgba(255,255,255,.6),transparent);pointer-events:none}
.lp-wrap{max-width:1240px;margin:0 auto;padding:0 28px;position:relative}
.lp-hero-grid{display:grid;grid-template-columns:minmax(0,5fr) minmax(0,7fr);gap:40px;align-items:center}
.lp-eyebrow{font-size:9.5px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#8a85ff;margin-bottom:12px}
.lp-eyebrow.dark{color:#4f60d8}
.lp-h1{font-size:clamp(34px,4.2vw,50px);font-weight:700;line-height:1.08;letter-spacing:-1.5px;margin-bottom:16px}
.lp-hero p.sub{font-size:14px;color:rgba(255,255,255,.75);max-width:340px;margin-bottom:22px}
.lp-flow{display:flex;align-items:flex-start;justify-content:space-between;gap:4px}
.lp-node{display:flex;flex-direction:column;align-items:center;width:84px;text-align:center}
.lp-node .box{width:70px;height:70px;border-radius:14px;border:1px solid rgba(160,155,255,.35);background:rgba(255,255,255,.05);backdrop-filter:blur(6px);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;font-size:9.5px;font-weight:500}
.lp-node.active .box{border-color:#8f86ff;background:rgba(109,94,240,.28);box-shadow:0 0 30px rgba(109,94,240,.6)}
.lp-node .cap{margin-top:12px;font-size:9px;color:rgba(255,255,255,.65);line-height:1.35}
.lp-arrow{margin-top:26px;color:rgba(255,255,255,.55);font-size:12px}
.lp-cards{max-width:1240px;margin:-92px auto 0;padding:0 28px;display:grid;grid-template-columns:repeat(3,1fr);gap:24px;position:relative;z-index:5}
.lp-card{background:#fff;border-radius:12px;padding:22px;box-shadow:0 12px 40px rgba(30,30,90,.14);border:1px solid #eceefa;position:relative;cursor:pointer;transition:transform .2s}
.lp-card:hover{transform:translateY(-3px)}
.lp-card .ic{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center}
.lp-card .chev{position:absolute;top:22px;right:22px;width:26px;height:26px;border-radius:50%;background:#f1f2fb;display:flex;align-items:center;justify-content:center;color:#5d6280}
.lp-sec{padding:64px 0}
.lp-h2{font-size:clamp(24px,3vw,32px);font-weight:700;letter-spacing:-1px;line-height:1.15;margin-bottom:10px}
.lp-muted{color:#5d6280;font-size:13px}
.lp-dom-grid{display:grid;grid-template-columns:minmax(0,4fr) minmax(0,7fr);gap:36px;align-items:start}
.lp-dom-row{display:grid;grid-template-columns:150px 1fr 240px;gap:16px;align-items:center;background:#fff;border:1px solid #eceefa;border-radius:12px;padding:12px 16px;margin-bottom:12px;min-height:84px}
.lp-dom-row .q{font-size:11px;color:#3a3f5c;border-left:1px solid #eceefa;padding-left:16px}
.lp-steps{display:grid;grid-template-columns:repeat(5,1fr);gap:20px;margin-top:28px}
.lp-step .num{width:28px;height:28px;border-radius:50%;background:#e8ecff;color:#3b4bd8;font-weight:600;font-size:11px;display:flex;align-items:center;justify-content:center;margin-bottom:12px}
.lp-plat{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:26px}
.lp-plat-card{border-radius:14px;padding:18px;border:1px solid #e6e8f6}
.lp-plat-item{display:flex;align-items:center;gap:10px;background:#fff;border:1px solid #eceefa;border-radius:10px;padding:11px 12px;margin-top:10px}
.lp-prev-grid{display:grid;grid-template-columns:minmax(0,4fr) minmax(0,7fr);gap:36px;align-items:center}
.lp-pill{border:1px solid #e1e3f4;background:#fff;border-radius:99px;padding:8px 18px;font-size:11px;font-weight:500;color:#3a3f5c;cursor:pointer}
.lp-pill.on{background:#f1eefe;border-color:#cfc8fb;color:#4b3fd0;font-weight:600;box-shadow:0 4px 14px rgba(109,94,240,.18)}
.lp-app{background:#fff;border-radius:14px;border:1px solid #e6e8f6;box-shadow:0 14px 40px rgba(30,30,90,.1);display:grid;grid-template-columns:130px 1fr;overflow:hidden;min-height:300px}
.lp-app aside{border-right:1px solid #eceefa;padding:14px 10px;font-size:10px;color:#5d6280}
.lp-app aside div.i{padding:7px 9px;border-radius:7px;margin-top:3px}
.lp-app aside div.i.on{background:#f1eefe;color:#4b3fd0;font-weight:600}
.lp-kan{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:12px}
.lp-kan .col{background:#f7f8fe;border-radius:8px;padding:8px}
.lp-kan .k{background:#fff;border:1px solid #eceefa;border-radius:7px;padding:7px 8px;margin-top:6px;font-size:9px;font-weight:600}
.lp-kan .k small{display:block;font-weight:400;color:#8a8fb0;font-size:8px}
.lp-try{background:#fff;border-top:1px solid #eceefa;border-bottom:1px solid #eceefa}
.lp-cta{background:radial-gradient(ellipse at 80% 100%,#2a2d8a 0%,#10123d 60%,#080a26 100%);color:#fff;padding:44px 0}
.lp-cta .lp-wrap{display:flex;align-items:center;justify-content:space-between;gap:24px;flex-wrap:wrap}
.lp-foot{background:#fff;padding:22px 0 14px;font-size:11px}
.lp-foot .row{display:flex;align-items:center;justify-content:space-between;gap:20px;flex-wrap:wrap}
.lp-install{max-width:640px;margin:0 auto 22px}
@media(max-width:1024px){
 .lp-hero-grid,.lp-dom-grid,.lp-prev-grid{grid-template-columns:1fr}
 .lp-flow{flex-wrap:wrap;gap:14px}.lp-arrow{display:none}
 .lp-cards,.lp-plat{grid-template-columns:1fr}
 .lp-steps{grid-template-columns:repeat(2,1fr)}
 .lp-dom-row{grid-template-columns:1fr}.lp-dom-row .q{border-left:none;padding-left:0}
 .lp-nav-links{display:none}
}
@media(max-width:768px){
 .demo-tabs{display:flex !important;overflow-x:auto;gap:8px !important;scrollbar-width:none}
 .demo-tabs button{flex:0 0 145px}
 .demo-input-grid,.demo-result-grid{grid-template-columns:1fr !important}
 .lp-app{grid-template-columns:1fr}.lp-app aside{display:none}
 .lp-kan{grid-template-columns:1fr}
}
.lp-logo{border-radius:8px;filter:hue-rotate(93deg) saturate(1.7) brightness(1.2);object-fit:contain}
`;

const avatarGrad = ["linear-gradient(135deg,#f6c7a3,#c98a64)", "linear-gradient(135deg,#d9b08c,#8d5a3b)", "linear-gradient(135deg,#f3d2b8,#b5785a)"];

function AgentMock({ k }) {
  if (k === "workforce") {
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
        <div style={{ flex: 1 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: avatarGrad[i] }} />
              <div style={{ flex: 1, height: 5, borderRadius: 4, background: "#e8eaf6" }} />
            </div>
          ))}
        </div>
        <div>
          {[["96% match", "#e3f8ee", "#0f9d6a"], ["88% match", "#f1f2fb", "#5d6280"], ["76% match", "#f1f2fb", "#5d6280"]].map(([t, bg, col]) => (
            <div key={t} style={{ background: bg, color: col, fontSize: 8.5, fontWeight: 600, padding: "3px 8px", borderRadius: 5, marginBottom: 5, textAlign: "center" }}>{t}</div>
          ))}
        </div>
      </div>
    );
  }
  if (k === "atlas") {
    return (
      <div style={{ position: "relative", height: 70 }}>
        <div style={{ position: "absolute", left: 0, top: 0, width: 150, height: 66, borderRadius: 8, background: "#141836", padding: 8 }}>
          {[80, 55, 70, 40, 62].map((w, i) => <div key={i} style={{ height: 4, width: `${w}%`, borderRadius: 3, background: i % 2 ? "#5b6bd8" : "#38b48b", marginBottom: 6 }} />)}
        </div>
        <div style={{ position: "absolute", right: 0, top: 14, background: "#fff", borderRadius: 8, boxShadow: "0 6px 20px rgba(30,30,90,.18)", padding: "7px 9px", display: "flex", gap: 6, alignItems: "center", fontSize: 8, fontWeight: 600 }}>
          <Sparkle size={12} color="#5b4be8" /><span>Root cause identified<br /><span style={{ fontWeight: 400, color: "#5d6280" }}>Proposed fix ready</span></span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ position: "relative", height: 70 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 7, height: 62, paddingRight: 20 }}>
        {[22, 30, 26, 40, 48, 38, 58].map((h, i) => <div key={i} style={{ flex: 1, height: h, borderRadius: 3, background: i === 6 ? "#4338ca" : "#aeb6f5" }} />)}
      </div>
      <div style={{ position: "absolute", right: 0, bottom: 0, background: "#fff", borderRadius: 8, boxShadow: "0 6px 20px rgba(30,30,90,.18)", padding: "6px 9px", display: "flex", gap: 6, alignItems: "center", fontSize: 8, fontWeight: 600 }}>
        <Sparkle size={12} color="#5b4be8" />Key drivers identified
      </div>
    </div>
  );
}

function PreviewBody({ tab }) {
  if (tab === "planner") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8, marginTop: 12 }}>
        {PLANNER.map(([d, items]) => (
          <div key={d} className="lp-kan" style={{ display: "block", margin: 0 }}>
            <div className="col"><b style={{ fontSize: 9 }}>{d}</b>{items.map(t => <div className="k" key={t}>{t}</div>)}</div>
          </div>
        ))}
      </div>
    );
  }
  if (tab === "timeline") {
    return (
      <div style={{ marginTop: 14 }}>
        {TIMELINE.map(([t, left, w, col]) => (
          <div key={t} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, marginBottom: 4, color: "#3a3f5c" }}>{t}</div>
            <div style={{ height: 10, background: "#f1f2fb", borderRadius: 6, position: "relative" }}>
              <div style={{ position: "absolute", left: `${left}%`, width: `${Math.min(w, 100 - left)}%`, top: 0, bottom: 0, background: col, borderRadius: 6 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="lp-kan">
      {KANBAN.map(col => (
        <div className="col" key={col.title}>
          <div style={{ fontSize: 9, color: "#5d6280", display: "flex", justifyContent: "space-between" }}><span>{col.title}</span><span>{col.n}</span></div>
          {col.cards.map(([t, tag, color]) => (
            <div className="k" key={t}><span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: color, marginRight: 5 }} />{t}<small>{tag}</small></div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LandingPage({ onLogin }) {

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState("login");

  // Anonymous landing-page playground: deliberately small and predictable.
  // The real backend/API quota should still be enforced server-side.
  const [activeDemo, setActiveDemo] = useState("research");
  const [demoInput, setDemoInput] = useState(DEMOS.research.defaultInput);
  const [demoRuns, setDemoRuns] = useState(5);
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoStep, setDemoStep] = useState(-1);
  const [demoComplete, setDemoComplete] = useState(false);
  const [demoError, setDemoError] = useState("");
  const [demoResultData, setDemoResultData] = useState(null);
  const [demoQuotaBlocked, setDemoQuotaBlocked] = useState(false);
  const [activeInstallTab, setActiveInstallTab] = useState("npm");
  const [copiedInstallKey, setCopiedInstallKey] = useState("");

  function openModal(tab) { setModalTab(tab); setModalOpen(true); }

  async function copyInstallCommand(key, value) {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedInstallKey(key);
      setTimeout(() => setCopiedInstallKey(""), 1500);
    } catch {
      setCopiedInstallKey("");
    }
  }

  function selectDemo(key) {
    if (demoRunning) return;
    setActiveDemo(key);
    setDemoInput(DEMOS[key].defaultInput);
    setDemoStep(-1);
    setDemoComplete(false);
    setDemoError("");
    setDemoResultData(null);
  }

  function mapStructuredResultToRows(items = []) {
    return items.slice(0, 3).map((item) => [item.label, item.value, item.note]);
  }

  function getDisplayResult() {
    if (demoResultData) {
      return {
        resultTitle: demoResultData.title,
        resultIntro: demoResultData.summary,
        resultRows: mapStructuredResultToRows(demoResultData.items),
        footer: demoResultData.nextActions?.length
          ? `Next: ${demoResultData.nextActions.join(" · ")}`
          : "Lyra completed your request",
      };
    }
    return DEMOS[activeDemo];
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API}/demo/usage`, {
          method: "GET",
          credentials: "include",
        });
        const data = await res.json();
        if (!mounted) return;
        if (res.ok && data?.usage && typeof data.usage.remaining === "number") {
          setDemoRuns(data.usage.remaining);
        }
      } catch {
        // Keep default value when usage lookup fails.
      }
    })();

    return () => { mounted = false; };
  }, []);

  async function runDemo() {
    if (demoRunning) return;
    if (demoRuns <= 0) {
      setDemoQuotaBlocked(true);
      setDemoError("You've used your 5 free Lyra runs for today.");
      return;
    }
    setDemoQuotaBlocked(false);
    setDemoError("");
    setDemoResultData(null);
    setDemoRunning(true);
    setDemoComplete(false);
    setDemoStep(0);

    const totalSteps = DEMOS[activeDemo].steps.length;
    let stepIndex = 0;
    let loopCancelled = false;

    const stepLoop = (async () => {
      while (!loopCancelled) {
        await new Promise(resolve => setTimeout(resolve, 650));
        if (loopCancelled) break;
        stepIndex = Math.min(stepIndex + 1, totalSteps - 1);
        setDemoStep(stepIndex);
      }
    })();

    try {
      const res = await fetch(`${API}/demo/run`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: activeDemo, input: demoInput.trim() }),
      });

      const data = await res.json();

      if (data?.usage && typeof data.usage.remaining === "number") {
        setDemoRuns(data.usage.remaining);
      }

      if (res.status === 429 || data?.error === "DAILY_DEMO_LIMIT_REACHED") {
        setDemoQuotaBlocked(true);
        setDemoError("You've used your 5 free Lyra runs for today.");
        setDemoRunning(false);
        setDemoStep(-1);
        return;
      }

      if (!res.ok || !data?.success) {
        throw new Error("DEMO_RUN_FAILED");
      }

      if (data?.result) {
        setDemoResultData(data.result);
      } else {
        setDemoResultData(null);
      }

      // Smoothly complete any remaining visible steps before revealing result.
      while (stepIndex < totalSteps - 1) {
        await new Promise(resolve => setTimeout(resolve, 250));
        stepIndex += 1;
        setDemoStep(stepIndex);
      }

      await new Promise(resolve => setTimeout(resolve, 220));
      setDemoRunning(false);
      setDemoComplete(true);
    } catch {
      setDemoError("Lyra is temporarily busy. Please try again.");
      setDemoRunning(false);
      setDemoStep(-1);
    } finally {
      loopCancelled = true;
      await stepLoop;
    }
  }

  const [previewTab, setPreviewTab] = useState("kanban");

  const c = {
    bg: "#f7f8fe", border: "#e4e6f4", border2: "#cbd0e8", blue4: "#d9d4fb",
    text: "#12142b", text2: "#12142b", muted: "#5d6280", soft: "#8a8fb0",
  };

  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = c.bg;
    return () => { document.body.style.background = prev || ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="lp">
      <style>{LP_CSS}</style>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} initialTab={modalTab} onLogin={onLogin} />

      {/* ── HERO ── */}
      <section className="lp-hero">
        <nav className="lp-nav">
          <a className="lp-brand" href="#top"><img src="/images/lyra-icon.png" alt="Agent Lyra" className="lp-logo" style={{ width: 30, height: 30 }} />Agent Lyra</a>
          <div className="lp-nav-links">
            <a href="#platform">Platform</a><a href="#agents">Domain Agents</a><a href="#trust">Trust</a>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 18, fontSize: 11 }}>
            <button onClick={() => openModal("login")} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 11 }}>Sign in</button>
            <button className="lp-btn" onClick={() => openModal("signup")}>Get started <ArrowRight size={13} /></button>
          </div>
        </nav>
        <div className="lp-wrap" id="top" style={{ paddingTop: 40 }}>
          <div className="lp-hero-grid">
            <FadeUp>
              <div className="lp-eyebrow">Your context. Real outcomes.</div>
              <h1 className="lp-h1">The operating layer for intelligent work.</h1>
              <p className="sub" style={{ fontSize: 14, color: "rgba(255,255,255,.75)", maxWidth: 340, marginBottom: 22 }}>Turn the context already inside your business into accountable action.</p>
              <button className="lp-btn" onClick={() => go("agents")}>Meet the agents <ArrowRight size={13} /></button>
            </FadeUp>
            <FadeUp delay={120}>
              <div className="lp-flow">
                {FLOW.map(({ Icon, label, cap, active }, i) => (
                  <div key={label} style={{ display: "contents" }}>
                    <div className={`lp-node${active ? " active" : ""}`}>
                      <div className="box"><Icon size={20} />{label}</div>
                      <div className="cap">{cap}</div>
                    </div>
                    {i < FLOW.length - 1 && <div className="lp-arrow">→</div>}
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ── AGENT CARDS ── */}
      <div className="lp-cards">
        {AGENT_CARDS.map(({ key, Icon, name, sub, desc, tint, color }) => (
          <div key={key} className="lp-card" onClick={() => go("agents")}>
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 14 }}>
              <div className="ic" style={{ background: tint, color }}><Icon size={22} /></div>
              <div><div style={{ fontWeight: 700, fontSize: 14 }}>{name}</div><div className="lp-muted" style={{ fontSize: 11 }}>{sub}</div></div>
            </div>
            <div className="chev"><ChevronRight size={14} /></div>
            <div className="lp-muted" style={{ fontSize: 11.5 }}>{desc}</div>
          </div>
        ))}
      </div>

      {/* ── DOMAIN AGENTS ── */}
      <section className="lp-sec" id="agents">
        <div className="lp-wrap lp-dom-grid">
          <FadeUp>
            <div className="lp-eyebrow dark">Domain agents</div>
            <h2 className="lp-h2">Domain expertise where work happens.</h2>
            <p className="lp-muted" style={{ maxWidth: 300 }}>Specialized agents, grounded in your business context, work alongside your teams in the tools you already use.</p>
          </FadeUp>
          <div>
            {[
              [AGENT_CARDS[0], "“Which candidates are most likely to succeed in this role?”"],
              [AGENT_CARDS[1], "“What's the fastest way to resolve this production issue?”"],
              [AGENT_CARDS[2], "“What drives the variance in this quarter's forecast?”"],
            ].map(([a, q], i) => (
              <FadeUp key={a.key} delay={i * 80}>
                <div className="lp-dom-row">
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div className="ic" style={{ width: 38, height: 38, borderRadius: 10, background: a.tint, color: a.color, display: "flex", alignItems: "center", justifyContent: "center" }}><a.Icon size={18} /></div>
                    <div><div style={{ fontWeight: 700, fontSize: 12.5 }}>{a.name}</div><div className="lp-muted" style={{ fontSize: 10 }}>{a.sub}</div></div>
                  </div>
                  <div className="q">{q}</div>
                  <AgentMock k={a.key} />
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="lp-sec" style={{ paddingTop: 20 }}>
        <div className="lp-wrap">
          <FadeUp>
            <div className="lp-eyebrow dark">How it works</div>
            <h2 className="lp-h2">From context to outcome.</h2>
            <p className="lp-muted">A simple, secure path from your existing data to real business results.</p>
          </FadeUp>
          <div className="lp-steps">
            {STEPS.map(([Icon, t, d], i) => (
              <FadeUp key={t} delay={i * 60}>
                <div className="lp-step">
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className="num">{i + 1}</div>
                    {i < 4 && <div style={{ flex: 1, borderTop: "1px dashed #c9cde8" }} />}
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <Icon size={18} color="#6d5ef0" style={{ flexShrink: 0, marginTop: 2 }} />
                    <div><div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 3 }}>{t}</div><div className="lp-muted" style={{ fontSize: 10.5 }}>{d}</div></div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORM ── */}
      <section className="lp-sec" id="platform" style={{ paddingTop: 20 }}>
        <div className="lp-wrap">
          <FadeUp>
            <div className="lp-eyebrow dark">The platform</div>
            <h2 className="lp-h2">Build, govern, execute.</h2>
            <p className="lp-muted">Everything you need to create value with AI agents — securely, at scale.</p>
          </FadeUp>
          <div className="lp-plat">
            {PLATFORM.map(({ title, sub, Icon, tint, color, items }, i) => (
              <FadeUp key={title} delay={i * 80}>
                <div className="lp-plat-card" style={{ background: tint }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <Icon size={28} color={color} />
                    <div><div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div><div className="lp-muted" style={{ fontSize: 10.5 }}>{sub}</div></div>
                  </div>
                  {items.map(([I, n, d]) => (
                    <div className="lp-plat-item" key={n}>
                      <I size={18} color={color} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 11.5 }}>{n}</div><div className="lp-muted" style={{ fontSize: 9.5 }}>{d}</div></div>
                      <ChevronRight size={13} color="#8a8fb0" />
                    </div>
                  ))}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRODUCT PREVIEW ── */}
      <section className="lp-sec" style={{ paddingTop: 20 }}>
        <div className="lp-wrap lp-prev-grid">
          <FadeUp>
            <div className="lp-eyebrow dark">Product preview</div>
            <h2 className="lp-h2">Work that moves forward.</h2>
            <p className="lp-muted" style={{ marginBottom: 20 }}>Plan, track, and execute work with your agents and teams in one place.</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[["kanban", "Kanban board"], ["planner", "Weekly planner"], ["timeline", "Schedule timeline"]].map(([k, l]) => (
                <button key={k} className={`lp-pill${previewTab === k ? " on" : ""}`} onClick={() => setPreviewTab(k)}>{l}</button>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={100}>
            <div className="lp-app">
              <aside>
                <div className="lp-brand" style={{ fontSize: 10.5, marginBottom: 10, color: "#12142b" }}><img src="/images/lyra-icon.png" alt="Agent Lyra" className="lp-logo" style={{ width: 18, height: 18 }} />Agent Lyra</div>
                {["Work", "Agents", "Knowledge", "Approvals", "Settings"].map((n, i) => <div key={n} className={`i${i === 0 ? " on" : ""}`}>{n}</div>)}
              </aside>
              <div style={{ padding: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 12 }}>Q2 Priorities</div>
                <PreviewBody tab={previewTab} />
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── TRY LYRA (live demo + install) ── */}
      <section className="lp-try lp-sec" id="try-section">
        <div className="lp-wrap">
          <FadeUp>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div className="lp-eyebrow dark">Try it now</div>
              <h2 className="lp-h2">Give Lyra something to do.</h2>
              <p className="lp-muted">No sign-up needed to explore.</p>
            </div>
          </FadeUp>
          <div className="lp-install" style={{ background: "#f5f6fd", border: "1px solid #e4e6f4", borderRadius: 14, padding: 12 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {Object.keys(INSTALL_COMMANDS).map((tab) => (
                <button key={tab} onClick={() => setActiveInstallTab(tab)} style={{ padding: "5px 12px", borderRadius: 99, border: `1px solid ${activeInstallTab === tab ? "#6d5ef0" : "#d9dcf0"}`, background: activeInstallTab === tab ? "#eeeafe" : "#fff", color: "#3a3f5c", fontSize: 11, fontWeight: 700, textTransform: "uppercase", cursor: "pointer" }}>{tab}</button>
              ))}
            </div>
            {["install", "init"].map((kind) => (
              <div key={kind} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: "#12142b", borderRadius: 9, padding: "9px 12px", marginTop: kind === "init" ? 8 : 0 }}>
                <span style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 12.5, color: kind === "init" ? "#c7d7ff" : "#c9f5e3" }}>{INSTALL_COMMANDS[activeInstallTab][kind]}</span>
                <button onClick={() => copyInstallCommand(`${activeInstallTab}-${kind}`, INSTALL_COMMANDS[activeInstallTab][kind])} style={{ background: "transparent", border: "none", color: "#c9f5e3", cursor: "pointer", display: "flex" }}>
                  {copiedInstallKey === `${activeInstallTab}-${kind}` ? <Check size={15} /> : <Copy size={15} />}
                </button>
              </div>
            ))}
          </div>
        <FadeUp delay={360} style={{ width: "100%", maxWidth: 1160, margin: "0 auto", position: "relative" }}>
          <div id="try" style={{ background: "var(--lyra-color-surface)", color: c.text, borderRadius: 24, border: "1px solid rgba(255,255,255,.18)", overflow: "hidden", boxShadow: "0 30px 90px rgba(0,0,0,.32)" }}>
            <div style={{ padding: "16px 20px", background: "var(--lyra-color-surface-2)", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(135deg,var(--app-primary-mid),#48d6ad)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--app-on-accent)" }}>
                  <Bot size={16} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>TRY LYRA</div>
                  <div style={{ fontSize: 11, color: c.soft }}>No sign-up required</div>
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: c.muted, fontWeight: 600 }}>
                {demoRuns > 0 ? `${demoRuns} / 5 free runs left today` : "0 / 5 free runs left today"}
              </div>
            </div>

            <div style={{ padding: 18 }}>
              {demoError && (
                <div style={{ marginBottom: 12, background: "var(--lyra-color-danger-bg)", border: "1px solid var(--app-danger-border)", borderRadius: 10, padding: "9px 11px", fontSize: 12, color: "var(--lyra-color-danger)" }}>
                  {demoError}
                </div>
              )}

              <div className="demo-tabs" style={{ display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: 8, marginBottom: 14 }}>
                {Object.entries(DEMOS).map(([key, demo]) => (
                  <button
                    key={key}
                    onClick={() => selectDemo(key)}
                    disabled={demoRunning}
                    style={{
                      padding: "11px 10px",
                      borderRadius: 12,
                      border: `1px solid ${activeDemo === key ? "#48b995" : c.border}`,
                      background: activeDemo === key ? "var(--lyra-color-surface-2)" : "var(--lyra-color-surface)",
                      color: activeDemo === key ? "#206b56" : c.text2,
                      cursor: demoRunning ? "not-allowed" : "pointer",
                      textAlign: "left",
                      fontFamily: "'DM Sans',sans-serif",
                      opacity: demoRunning && activeDemo !== key ? .55 : 1,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 800 }}>
                      <span style={{ display: "flex" }}>{demo.icon}</span>{demo.label}
                    </div>
                    <div style={{ fontSize: 10.5, color: c.soft, marginTop: 3 }}>{demo.subtitle}</div>
                  </button>
                ))}
              </div>

              <div className="demo-input-grid" style={{ display: "grid", gridTemplateColumns: "1fr 250px", gap: 14, alignItems: "stretch" }}>
                <div style={{ border: `1px solid ${c.border}`, borderRadius: 16, padding: 16, background: "var(--lyra-color-surface)" }}>
                  <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 5 }}>{DEMOS[activeDemo].title}</div>
                  <div style={{ fontSize: 12, color: c.muted, marginBottom: 12 }}>Tell Lyra what you want done.</div>
                  <textarea
                    value={demoInput}
                    onChange={e => { setDemoInput(e.target.value); setDemoComplete(false); }}
                    placeholder={DEMOS[activeDemo].placeholder}
                    disabled={demoRunning}
                    rows={4}
                    style={{ width: "100%", resize: "vertical", minHeight: 96, border: `1px solid ${c.border}`, borderRadius: 11, padding: 12, fontSize: 13, lineHeight: 1.55, color: c.text, background: "var(--lyra-color-surface-2)", outline: "none", fontFamily: "'DM Sans',sans-serif" }}
                  />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 10 }}>
                    <span style={{ fontSize: 10.5, color: c.soft }}>Tip: be specific about the outcome you want.</span>
                    <button
                      className="btn-hero"
                      onClick={runDemo}
                      disabled={demoRunning}
                      style={{ padding: "11px 20px", borderRadius: 10, border: "none", fontSize: 12.5, fontWeight: 800, color: "var(--app-on-accent)", background: demoRuns <= 0 ? "#94a3b8" : "var(--app-primary-mid)", cursor: demoRunning ? "not-allowed" : "pointer", fontFamily: "'DM Sans',sans-serif", boxShadow: "0 6px 18px rgba(58,107,92,.25)", transition: "all .2s" }}
                    >
                      {demoRunning ? "Lyra is working…" : demoRuns <= 0 ? "Free runs used" : "Run with Lyra →"}
                    </button>
                  </div>
                </div>

                <div style={{ border: `1px solid ${c.border}`, borderRadius: 16, padding: 16, background: "var(--lyra-color-surface-2)" }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, color: c.soft, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 12 }}>Agent in action</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                    {DEMOS[activeDemo].steps.map((step, i) => {
                      const done = demoStep > i || demoComplete;
                      const current = demoRunning && demoStep === i;
                      return (
                        <div key={step} style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                          <div style={{ width: 19, height: 19, borderRadius: "50%", border: `1.5px solid ${done ? "#2e9f7b" : current ? "var(--app-primary-mid)" : c.border2}`, background: done ? "var(--lyra-color-surface-2)" : current ? "var(--lyra-color-surface-2)" : "var(--lyra-color-surface)", color: done ? "#248464" : c.soft, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 10, fontWeight: 800 }}>
                            {done ? "✓" : current ? "…" : i + 1}
                          </div>
                          <div>
                            <div style={{ fontSize: 11.5, fontWeight: 700, color: done || current ? c.text2 : c.soft }}>{step}</div>
                            {current && <div style={{ fontSize: 10, color: "var(--app-primary-mid)", marginTop: 1 }}>Working on it…</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${c.border}`, fontSize: 10.5, color: c.soft }}>
                    Goal → plan → tools → reasoning → result
                  </div>
                </div>
              </div>

              {demoComplete && (
                <div style={{ marginTop: 14, border: `1px solid var(--lyra-color-success-bg)`, borderRadius: 16, background: "linear-gradient(180deg,var(--lyra-color-surface),var(--lyra-color-surface-2))", overflow: "hidden" }}>
                  {(() => {
                    const shown = getDisplayResult();
                    return (
                      <>
                  <div style={{ padding: "15px 16px 12px", borderBottom: `1px solid var(--app-surface-3)`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>{shown.resultTitle}</div>
                      <div style={{ fontSize: 11.5, color: c.muted, marginTop: 4, maxWidth: 760 }}>{shown.resultIntro}</div>
                    </div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "#248464", background: "var(--lyra-color-surface-2)", padding: "5px 8px", borderRadius: 7, whiteSpace: "nowrap" }}>
                      {demoResultData ? "AI" : "Static fallback"}
                    </div>
                  </div>

                  <div className="demo-result-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 10, padding: 14 }}>
                    {shown.resultRows.map((row, i) => (
                      <div key={i} style={{ background: "var(--lyra-color-surface)", border: `1px solid ${c.border}`, borderRadius: 11, padding: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: "var(--app-primary-mid)", marginBottom: 5 }}>{row[0]}</div>
                        <div style={{ fontSize: 11.5, fontWeight: 800, color: c.text2, marginBottom: 4 }}>{row[1]}</div>
                        <div style={{ fontSize: 10.5, lineHeight: 1.45, color: c.muted }}>{row[2]}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ padding: "0 14px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10.5, color: c.soft }}>{shown.footer}</span>
                    <div className="demo-actions" style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                      {["Compare in detail", "Create action plan", "Run this automatically"].map(action => (
                        <button key={action} onClick={() => openModal("signup")} style={{ padding: "7px 10px", borderRadius: 8, border: `1px solid ${c.border2}`, background: "var(--lyra-color-surface)", color: c.text2, fontSize: 10.5, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
                          {action}
                        </button>
                      ))}
                    </div>
                  </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {demoQuotaBlocked && !demoRunning && (
                <div style={{ marginTop: 14, padding: 15, borderRadius: 14, border: `1px solid ${c.blue4}`, background: "var(--lyra-color-surface-2)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>You've seen what Lyra can do.</div>
                    <div style={{ fontSize: 11.5, color: c.muted, marginTop: 3 }}>Sign in to keep going, save agents and let them run without you.</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => openModal("signup")} style={{ padding: "9px 14px", borderRadius: 9, border: "none", background: "var(--app-primary-mid)", color: "var(--app-on-accent)", fontSize: 11.5, fontWeight: 800, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Continue with Lyra</button>
                    <button onClick={() => openModal("login")} style={{ padding: "9px 14px", borderRadius: 9, border: `1px solid ${c.border2}`, background: "var(--lyra-color-surface)", color: c.text2, fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Log in</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </FadeUp>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="trust" className="lp-cta">
        <div className="lp-wrap">
          <div>
            <div className="lp-eyebrow">Built for what's next</div>
            <h2 className="lp-h2" style={{ marginBottom: 6 }}>Put intelligence to work—with control.</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,.75)" }}>Empower your teams, use your context, and get real results.</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <button className="lp-btn" onClick={() => openModal("signup")} style={{ padding: "14px 40px" }}>Get started <ArrowRight size={13} /></button>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,.7)", marginTop: 12 }}>Secure. Scalable. Built for your business.</div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="lp-foot">
        <div className="lp-wrap">
          <div className="row">
            <a className="lp-brand" href="#top"><img src="/images/lyra-icon.png" alt="Agent Lyra" className="lp-logo" style={{ width: 26, height: 26 }} />Agent Lyra</a>
            <div style={{ display: "flex", gap: 28, color: "#3a3f5c" }}>
              <a href="#platform">Platform</a><a href="#agents">Domain Agents</a><a href="#trust">Trust</a>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
              <button onClick={() => openModal("login")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11 }}>Sign in</button>
              <button className="lp-btn" onClick={() => openModal("signup")} style={{ background: "#eeeafe", color: "#4b3fd0", boxShadow: "none" }}>Get started <ArrowRight size={13} /></button>
            </div>
          </div>
          <div className="row" style={{ marginTop: 14, color: "#8a8fb0", fontSize: 10 }}>
            <span />
            <div style={{ display: "flex", gap: 18 }}>
              <a href="/privacy-policy.html">Privacy</a><a href="/terms-of-service.html">Terms</a><a href="mailto:support@agentlyra.in">Contact</a>
            </div>
            <span>© 2026 Agent Lyra. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
